import { Component, Input, OnChanges, ViewChild, inject } from '@angular/core';
import { CompanyService } from '../../../services/company.service';
import { SmartphotoService } from '../../../services/smartphoto.service';
import { CardFieldWithValue } from '../../../entities/types/smartphoto-card-field.type';
import { firstValueFrom } from 'rxjs';
import { InfoFieldWithValue } from '../../../entities/types/smartphoto-info-field.type';
import { CaseService } from '../../../services/case.service';
import { ICompany } from '@bdeo-platform/private-api';
import { I18NEXT_SERVICE } from 'angular-i18next';
import { SmartphotoBasketEditDrawerComponent } from './smartphoto-basket-edit-drawer/smartphoto-basket-edit-drawer.component';
import { DisplayConditions, Smartphoto } from '@bdeo-motor/smartphoto-entities';

type InfoField = Exclude<Exclude<ICompany['brokersInfo'], undefined>['infoFields'], undefined>[number];

@Component({
  selector: 'app-smartphoto-info',
  templateUrl: './smartphoto-info.component.html',
  styleUrls: ['./smartphoto-info.component.scss']
})
export class SmartphotoInfoComponent implements OnChanges {
  private readonly smartphotoService = inject(SmartphotoService);
  private readonly companyService = inject(CompanyService);
  private readonly caseService = inject(CaseService);
  protected readonly i18next = inject(I18NEXT_SERVICE);

  @Input({ required: true }) smartphoto?: Smartphoto;

  public company$ = this.companyService.getCurrentCompanyObservable();
  public statusDetails$ = this.smartphotoService.statusDetails$;
  public hasDeadline$ = this.companyService.hasDeadline$;

  public cardFields: CardFieldWithValue[] = [];
  public infoFields: InfoFieldWithValue[] = [];
  public canShowBasket = false;
  public canShowEditBasket = false;
  public loading = true;

  @ViewChild(SmartphotoBasketEditDrawerComponent) protected drawer!: SmartphotoBasketEditDrawerComponent;

  public async ngOnChanges(): Promise<void> {
    if (!this.smartphoto) {
      return;
    }

    const [company, caseObject] = await Promise.all([firstValueFrom(this.company$), this.caseService.getByNumber(this.smartphoto.caseRef)]);

    if (!company || !caseObject) {
      return;
    }

    const smartphotoOriginal = this.smartphotoService.getOriginalSmartphoto();

    if (!smartphotoOriginal) {
      return;
    }

    this.canShowBasket = Boolean(company?.brokersInfo?.showEstimation) && DisplayConditions.canShowBasket(this.smartphoto, company);
    this.canShowEditBasket = DisplayConditions.canShowEditBasket(company);

    const smartphotoMap = new Map(Object.entries(smartphotoOriginal));
    const cardFields = company.brokersInfo?.cardFields;
    const infoFields = company.brokersInfo?.infoFields;
    const caseFieldsMap = new Map(Object.entries(caseObject));

    this.cardFields =
      cardFields
        ?.map((field) => ({
          ...field,
          value: field.origin === 'case' ? caseFieldsMap.get(field.fieldName) : smartphotoMap.get(field.fieldName)
        }))
        .filter((f) => f.value) ?? [];

    this.infoFields = (
      await Promise.all(
        infoFields?.map(async (field) => ({
          ...field,
          value: await this.getFieldValue(field, caseFieldsMap, smartphotoMap)
        })) ?? []
      )
    ).filter((f) => f.value);

    const claimType = this.smartphoto.claimType;

    if (claimType) {
      this.infoFields?.push({
        fieldName: 'claimType',
        fieldDescription: this.i18next.t('brokers:claim_type'),
        origin: 'selfadjust',
        dataType: 'list',
        value: company.smartphoto.claimTypes?.find((type) => type.value === claimType)?.name
      });
    }

    this.loading = false;
  }

  private async getFieldValue(
    field: InfoField,
    caseFieldsMap: Map<string, unknown>,
    smartphotoMap: Map<string, unknown>
  ): Promise<unknown> {
    const fieldValue = field.origin === 'case' ? caseFieldsMap.get(field.fieldName) : smartphotoMap.get(field.fieldName);
    if (field.dataType === 'list') {
      if (!field.listName) {
        throw new Error('Info field list does not have listName configured!');
      }
      const selectOptions = await this.companyService.getSelectorValues(field.listName);
      return selectOptions.find((option) => option.value === fieldValue)?.text;
    } else {
      return fieldValue;
    }
  }
}
